import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Stack } from "@mui/material";
import { AsyncContentMui } from "components";
import { Chart, InfoCard } from "components/reports";
import { ReportMeasuresTable } from "components/reports/ReportMeasuresTable/ReportMeasuresTable";
import { useActionHook } from "hooks";
import {
  useClientGraphDataFormSelector,
  useClientReportTableColumnsSelector,
  useClientSnapshotCubicConfigSelector,
  useReportsActions,
  useReportsQueriesSelector,
  useUserActions
} from "store";
import type { ReportFilters, ReportTableColumn } from "store/types";

interface Props {
  clientId: number;
}

/**
 * Client Report tab — the Portfolio Analysis snapshot (overview only), scoped to one client
 * via a `client_id` filter and driven by the client_* login settings
 * (`cubicClientSnapshot` / `clientGraphDataFormation` / `clientReportTableColumns`).
 */
export const ClientReportSnapshot: React.FC<Props> = ({ clientId }) => {
  const [metaLoading, setMetaLoading] = useState<boolean>(true);
  const {
    dateRange,
    exchangeMethod,
    measures,
    groupBy,
    calculatedFields,
    indexName,
    rowBy,
    currency,
    originalCurrency,
    characteristics,
    use_accounting_date,
    filters: queryFilters
  } = useReportsQueriesSelector();

  const cubicSettings = useClientSnapshotCubicConfigSelector();
  const graphDataFormation = useClientGraphDataFormSelector();
  const reportTableColumns = useClientReportTableColumnsSelector();

  const { getReportTableAction, getCubicAction, getMetaAction, setQueriesAction, getBulkPieChartsAction } =
    useReportsActions();
  const { updateClientSnapshotTableColumnsAction } = useUserActions();

  // Merge the QueryManager filters with the client scope so both apply.
  const filters = useMemo<ReportFilters>(() => ({ ...queryFilters, client_id: [clientId] }), [queryFilters, clientId]);

  const getMeta = useCallback(async () => {
    try {
      setMetaLoading(true);
      await getMetaAction();
    } catch (e) {
      console.error("[ClientReportSnapshot] Failed to load meta:", e);
    } finally {
      setMetaLoading(false);
    }
  }, [getMetaAction]);

  useEffect(() => {
    getMeta();
  }, [getMeta]);

  const { action: refetch, loading } = useActionHook(() =>
    getReportTableAction({
      dateRange,
      measures,
      groupBy,
      columnBy: "measure",
      calculatedFields: [],
      indexName,
      rowBy,
      currency,
      originalCurrency,
      characteristics,
      filters,
      exchangeMethod,
      columns: reportTableColumns,
      use_accounting_date,
      show_count_rows: true
    })
  );

  const { data: cubicData, action: getCubic, loading: cubicLoading } = useActionHook(getCubicAction);

  const {
    data: bulkChartsData = {},
    action: fetchBulkCharts,
    loading: bulkChartsLoading
  } = useActionHook(getBulkPieChartsAction);

  // KPI cubics
  useEffect(() => {
    if (!Object.keys(cubicSettings).length) return;
    getCubic({
      cubics: cubicSettings,
      pageType: "cubicClientSnapshot",
      filters,
      exchangeMethod,
      currency,
      dateRange,
      use_accounting_date
    });
  }, [cubicSettings, dateRange, exchangeMethod, currency, filters, use_accounting_date]);

  // Table
  useEffect(() => {
    refetch();
  }, [
    exchangeMethod,
    dateRange,
    measures,
    groupBy,
    calculatedFields,
    indexName,
    rowBy,
    currency,
    originalCurrency,
    characteristics,
    filters,
    reportTableColumns,
    use_accounting_date
  ]);

  // Charts
  useEffect(() => {
    fetchBulkCharts({
      filters,
      graphDataFormation,
      dateRange,
      currency,
      exchangeMethod,
      use_accounting_date,
      pageType: "chartClientSnapshot"
    });
  }, [filters, graphDataFormation, dateRange, currency, exchangeMethod, use_accounting_date]);

  // Column drag/parameter changes: update the client-scoped redux slice (so the table refetches)
  // and persist to /user/settings under the "clientSnapshot" key (mirrors the deal "snapshot" key).
  const handleColumnsChange = useCallback(
    (columns: ReportTableColumn[]) => {
      setQueriesAction({ clientReportTableColumns: columns });
      updateClientSnapshotTableColumnsAction(columns);
    },
    [setQueriesAction, updateClientSnapshotTableColumnsAction]
  );

  return (
    <AsyncContentMui isLoading={metaLoading} spinnerProps={{ size: 150 }}>
      <Stack sx={{ gap: "16px" }}>
        <Stack direction="row" sx={{ gap: "10px", justifyContent: "space-between", width: "100%" }}>
          {cubicData &&
            Object.entries(cubicData).map(([name, data], index) => (
              <InfoCard pageType="cubicClientSnapshot" isLoading={cubicLoading} name={name} {...data} key={index} />
            ))}
        </Stack>
        <Stack direction="row" sx={{ gap: "10px" }}>
          {Object.entries(bulkChartsData).map(([name, data]) => (
            <Chart
              key={name}
              name={name}
              chartSettings={graphDataFormation[name]}
              filters={filters}
              pageType="chartClientSnapshot"
              isLoading={bulkChartsLoading}
              data={data}
            />
          ))}
        </Stack>
        <ReportMeasuresTable
          isLoading={loading}
          refetchReport={refetch}
          columns={reportTableColumns}
          onColumnsChange={handleColumnsChange}
        />
      </Stack>
    </AsyncContentMui>
  );
};

export default ClientReportSnapshot;
